import React from 'react';
import SectionCard from './SectionCard';
import {
  buildActivityNarrative,
  formatWeekStart,
  groupWeeksByMonth,
  plural,
  weekStartParts,
} from '../utils/narrative';

const CHART_H = 132;
const RECENT_WEEKS = 3;

// Lesson-section activity per week, 12 buckets, oldest on the left. The API
// counts section views (not lessons completed), so the card labels them as
// sections rather than borrowing the spec's wording.
export default function StudyActivityCard({ data, isVisible }) {
  const weeks = Array.isArray(data?.weeklyActivity) ? data.weeklyActivity : [];
  if (weeks.length === 0) return null;

  const counts = weeks.map((w) => Number(w?.activityCount) || 0);
  const total = counts.reduce((a, b) => a + b, 0);
  if (total === 0) return null;

  const max = Math.max(...counts);
  const average = total / counts.length;
  const averageOffset = CHART_H - (average / max) * CHART_H;
  const months = groupWeeksByMonth(weeks);
  const firstLabel = formatWeekStart(weeks[0]?.weekStart);
  const lastLabel = formatWeekStart(weeks[weeks.length - 1]?.weekStart);

  return (
    <SectionCard
      title={`Study activity, last ${weeks.length} weeks`}
      meta={firstLabel && lastLabel ? `${firstLabel} – ${lastLabel} · lesson sections opened per week` : undefined}
      lede={buildActivityNarrative(weeks, {
        peakLearningDay: data?.peakLearningDay,
        peakLearningHour: data?.peakLearningHour,
      })}
      style={{ marginBottom: 18 }}
    >
      <div style={{ paddingLeft: 30, position: 'relative' }}>
        <div
          className="ins-bar-row"
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            height: CHART_H,
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          {/* Average reference line — hairline, solid, recessive. */}
          <div
            style={{
              position: 'absolute',
              left: 30,
              right: 0,
              top: averageOffset,
              borderTop: '1px solid var(--border-hover)',
              pointerEvents: 'none',
            }}
          />
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: Math.max(averageOffset - 7, 0),
              fontSize: 10,
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-tertiary)',
            }}
          >
            avg {Math.round(average)}
          </span>
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: -4,
              fontSize: 10,
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-tertiary)',
            }}
          >
            {max}
          </span>

          {weeks.map((week, i) => {
            const count = counts[i];
            const isRecent = i >= weeks.length - RECENT_WEEKS;
            const height = count === 0 ? 3 : Math.max(4, (count / max) * CHART_H);
            return (
              <div
                key={week.weekStart || i}
                // Positioned so the bar paints over the average reference line.
                style={{ flex: 1, display: 'flex', justifyContent: 'center', position: 'relative' }}
              >
                <div
                  title={`Week of ${formatWeekStart(week.weekStart)}: ${count} ${plural(count, 'section')}, ${
                    week.quizzesPassed || 0
                  } ${plural(week.quizzesPassed || 0, 'quiz', 'quizzes')} passed`}
                  style={{
                    width: '100%',
                    maxWidth: 24,
                    height: isVisible ? height : 0,
                    background: count === 0 ? 'var(--border-hover)' : 'var(--accent-primary)',
                    opacity: count === 0 || isRecent ? 1 : 0.45,
                    borderRadius: '4px 4px 0 0',
                    transition: `height 0.5s ease ${0.1 + i * 0.03}s`,
                  }}
                />
              </div>
            );
          })}
        </div>

        <div className="ins-bar-row ins-week-days" style={{ display: 'flex', marginTop: 8 }}>
          {weeks.map((week, i) => {
            const parts = weekStartParts(week?.weekStart);
            return (
              <span
                key={week.weekStart || i}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: 10.5,
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--text-tertiary)',
                }}
              >
                {parts ? parts.day : ''}
              </span>
            );
          })}
        </div>

        <div className="ins-bar-row" style={{ display: 'flex', marginTop: 2 }}>
          {months.map((month, i) => (
            <span
              key={`${month.label}-${i}`}
              style={{ flex: month.span, fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)' }}
            >
              {month.label}
            </span>
          ))}
        </div>
      </div>

      <div className="ins-legend">
        <LegendSwatch color="var(--accent-primary)" label={`Last ${RECENT_WEEKS} weeks`} />
        <LegendSwatch color="var(--accent-primary)" opacity={0.45} label="Earlier weeks" />
        <LegendSwatch color="var(--border-hover)" label="No activity" />
      </div>
    </SectionCard>
  );
}

function LegendSwatch({ color, opacity, label }) {
  return (
    <span className="ins-legend-item">
      <span style={{ width: 10, height: 10, borderRadius: 2, background: color, opacity }} />
      {label}
    </span>
  );
}
